import * as React from 'react';
import { FunctionComponent, memo, useEffect, useState } from 'react';
import {
  BundlesMonitorCollapseProps,
  MonitorLiveViewModes,
  Types,
} from '../Monitor.types';
import {
  createStrokeColorForProgress,
  getBundleEtaDelay,
} from '../../../../utils/bundlesMonitorUtils';
import { InstructionType } from '../../../../types/bundleTypes';
import {
  convertGeoZoneToRegion,
  Order,
  OrderMonitor,
  Region,
} from '../../../../types/orderTypes';
import {
  FaExchangeAlt,
  FaExclamationTriangle,
  FaMapMarkerAlt,
  FaUserCheck,
} from 'react-icons/fa';
import { MdCallSplit } from 'react-icons/all';
import { TooltipComponent } from '../../../../ReusableComponents/Tooltip/TooltipComponent';
import { PopoverComponent } from '../../../../ReusableComponents/Popover/PopoverComponent';
import { LineProgressComponent } from '../../../../ReusableComponents/Progress/ProgressComponent';
import { MonitorOrdersTableWrapper } from '../MonitorOrdersTable/MonitorOrdersTableWrapper';
import _ from 'lodash';
import { Loader } from '../../../../components/Loader/Loader';
import { useDeepCompareEffect } from 'use-deep-compare';
import './MonitorCollapse.scss';
import clsx from 'clsx';
import dayjs from 'dayjs';

function getTheZonesInfo(orders: (Order | OrderMonitor)[]): string {
  if (orders.length === 0) {
    return '-';
  }
  return Array.from(new Set(orders.map(order => order.geoCode))).join(', ');
}

function getProductInfo(orders: (Order | OrderMonitor)[]): string {
  if (orders.length === 0) {
    return '-';
  }
  return Object.values(orders)[0].product;
}

const checkProps = (
  prevProps: Readonly<BundlesMonitorCollapseProps>,
  nextProps: Readonly<BundlesMonitorCollapseProps>
) =>
  !!(
    _.isEqual(prevProps.currentBundle, nextProps.currentBundle) &&
    _.isEqual(prevProps.currentOrders, nextProps.currentOrders) &&
    prevProps.bundleId === nextProps.bundleId &&
    prevProps.bundleDeliveryStatus === nextProps.bundleDeliveryStatus &&
    prevProps.isBundleOpen === nextProps.isBundleOpen &&
    prevProps.isBundleLoading === nextProps.isBundleLoading &&
    prevProps.lastViewMode === nextProps.lastViewMode &&
    _.isEqual(prevProps.lastSelectedOrders, nextProps.lastSelectedOrders) &&
    _.isEqual(prevProps.lastSelectedOrders, nextProps.lastSelectedOrders) &&
    prevProps.region === nextProps.region
  );

const MonitorCollapseWrapper: FunctionComponent<BundlesMonitorCollapseProps> =
  memo(
    ({
      region,
      bundle,
      bundleId,
      currentBundle,
      currentOrders,
      dispatch,
      updateSelectedBundleOrBundlesCallback,
      expandedCallback,
      isBundleLoading,
      bundleDeliveryStatus = 'not-started',
      isBundleOpen,
      toggleMapModal,
      toggleETAModal,
      toggleShelfModal,
      toggleCourierModal,
      lastViewMode,
      lastSelectedOrders,
      toggleMoveOrdersModal,
      togglePickupPointModal,
    }) => {
      const [bundleViewMode, setBundleViewMode] =
        useState<MonitorLiveViewModes>(lastViewMode);
      const [anchor, setAnchor] = useState<HTMLElement>(null);
      const handleClickPopover = (event: React.MouseEvent<HTMLElement>) => {
        setAnchor(event.currentTarget);
      };
      const onClosePopover = () => setAnchor(null);
      const isPopoverOpen = Boolean(anchor);

      useDeepCompareEffect(() => {
        if (lastViewMode !== bundleViewMode) {
          setBundleViewMode(lastViewMode);
        }
      }, [lastViewMode]);

      if (!currentBundle || !currentOrders) {
        return null;
      }

      const orders = currentOrders
        ?.map(orderInstruction => orderInstruction.order)
        .filter(order => order);
      const ordersCompleted = currentBundle.instructions.filter(
        instr => instr.type === InstructionType.Delivery && instr.completed
      ).length;
      const ordersWithDeviationsLinks = Array.from(
        new Set(
          orders
            .filter(order => order.openCourierDeviation)
            .map(order => order.id)
        )
      ).map(id => (
        <a
          key={id}
          href={`/orders/${id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="monitor-deviations-popover-item"
        >
          {id}
        </a>
      ));
      const completedPickups = currentBundle.instructions.filter(
        pickup => pickup.type === InstructionType.Pickup && pickup.completed
      );
      const strokeColor = createStrokeColorForProgress(bundleDeliveryStatus);
      const completedPctDeliveries =
        (ordersCompleted / (currentBundle.instructions.length / 2)) * 100 ||
        (1 / (currentBundle.instructions.length / 2)) * 100; //show progress bar if first order is delayed
      const geoZoneRegion =
        region ||
        convertGeoZoneToRegion(orders.length ? orders[0].geoZone : Region.OSLO);
      const { instructions } = currentBundle;
      const bundleLength = instructions.length - 1;
      const etaDelay = getBundleEtaDelay(currentOrders);
      const dayjsLastEta = instructions[bundleLength]?.eta?.isValid()
        ? etaDelay
          ? instructions[bundleLength].eta.subtract(
              etaDelay.delayMinutes,
              'minute'
            )
          : instructions[bundleLength].eta
        : null;
      const lastEta =
        dayjsLastEta && dayjsLastEta?.isValid()
          ? dayjsLastEta.format('HH:mm')
          : '-';
      const lastOrderInstruction = currentOrders[currentOrders.length - 1];
      let lastOrderInDanger = false;
      if (
        lastOrderInstruction &&
        dayjsLastEta?.isValid() &&
        dayjs(lastOrderInstruction.order?.deliverUntil)?.isValid() &&
        dayjsLastEta?.isAfter(dayjs(lastOrderInstruction.order?.deliverUntil))
      ) {
        lastOrderInDanger = true;
      }

      return (
        <div className="bundle-monitor-accordion-container">
          {isBundleLoading ? (
            <div className="loader-container">
              <Loader size="large" type="spinner" />{' '}
            </div>
          ) : null}
          <div
            className="monitor-bundle-container"
            onClick={() => {
              expandedCallback(bundleId);
            }}
          >
            <div className="content-container courier">
              {currentBundle?.assignedCourierName?.length > 40 ? (
                <TooltipComponent
                  className="custom-tooltip-container"
                  id={currentBundle?.assignedCourierName}
                  title={currentBundle?.assignedCourierName}
                  placement="top-start"
                >
                  <p
                    className="courier-data"
                    onClick={e => {
                      e.stopPropagation();
                      toggleCourierModal(prevState => ({
                        isOpen: !prevState.isOpen,
                        bundleId,
                      }));
                    }}
                  >
                    {currentBundle?.assignedCourierName}
                  </p>
                </TooltipComponent>
              ) : (
                <p
                  className="courier-data"
                  onClick={e => {
                    e.stopPropagation();
                    toggleCourierModal(prevState => ({
                      isOpen: !prevState.isOpen,
                      bundleId,
                    }));
                  }}
                >
                  {currentBundle?.assignedCourierName}
                </p>
              )}
            </div>
            <div className="content-container product">
              <span>{getProductInfo(orders)}</span>
            </div>
            <div className="content-container shelf">
              <span>{currentBundle?.shelfId}</span>
            </div>
            <div className="content-container pick-ups">
              <span>{`${completedPickups.length}/${
                currentBundle.instructions.length / 2
              }`}</span>
            </div>
            <div className="content-container zones">
              <span>{getTheZonesInfo(orders)}</span>
            </div>
            <div className="content-container bundles">
              <a
                onClick={e => e.stopPropagation()}
                href={`bundles/${bundleId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {bundleId}
              </a>
            </div>
            <div className="content-container deliveries">
              <span>{`${ordersCompleted}/${
                currentBundle.instructions.length / 2
              }`}</span>
            </div>
            <div
              className={clsx(
                'content-container',
                'last-eta',
                lastOrderInDanger && 'danger-eta'
              )}
            >
              <span>{lastEta}</span>
            </div>
            {!ordersWithDeviationsLinks?.length ? (
              <div className={'content-container deviations'}>
                <span>0</span>
              </div>
            ) : (
              <div
                className={'content-container deviations'}
                onClick={e => e.stopPropagation()}
              >
                <span
                  className="popover-link"
                  onClick={e => {
                    e.stopPropagation();
                    handleClickPopover(e);
                  }}
                >
                  <FaExclamationTriangle
                    style={{ paddingTop: '4px', color: 'orange' }}
                  />{' '}
                  {ordersWithDeviationsLinks?.length}
                </span>
                <PopoverComponent
                  id={bundleId}
                  anchorEl={anchor}
                  open={isPopoverOpen}
                  onClose={onClosePopover}
                >
                  {ordersWithDeviationsLinks}
                </PopoverComponent>
              </div>
            )}
            <div className="content-container icons">
              <TooltipComponent
                title="Show map"
                id="map-tooltip"
                placement="top"
              >
                <FaMapMarkerAlt
                  onClick={e => {
                    e.stopPropagation();
                    toggleMapModal(prevState => ({
                      isOpen: !prevState.isOpen,
                      bundleId,
                    }));
                  }}
                />
              </TooltipComponent>
              <TooltipComponent
                title="Select courier"
                id="select-courier-tooltip"
                placement="top"
              >
                <FaUserCheck
                  onClick={e => {
                    e.stopPropagation();
                    toggleCourierModal(prevState => ({
                      isOpen: !prevState.isOpen,
                      bundleId,
                    }));
                  }}
                />
              </TooltipComponent>
              <TooltipComponent
                title="Split bundles"
                id="split-tooltip"
                placement="top"
              >
                <MdCallSplit
                  onClick={event => {
                    event.stopPropagation();
                    if (!isBundleOpen) {
                      dispatch({
                        type: Types.StoreLastChangesInBundle,
                        payload: { bundleId, view: 'split' },
                      });
                      expandedCallback(bundleId, 'collapse');
                    } else {
                      setBundleViewMode(currentMode =>
                        currentMode === 'split' ? 'normal' : 'split'
                      );
                    }
                  }}
                />
              </TooltipComponent>
              <TooltipComponent
                id="move-tooltip"
                placement="top"
                title="Move orders to other bundle"
              >
                <FaExchangeAlt
                  className="move-icon"
                  onClick={event => {
                    event.stopPropagation();
                    if (!isBundleOpen) {
                      dispatch({
                        type: Types.StoreLastChangesInBundle,
                        payload: { bundleId, view: 'move' },
                      });
                      expandedCallback(bundleId, 'collapse');
                    } else {
                      setBundleViewMode(currentMode =>
                        currentMode === 'move' ? 'normal' : 'move'
                      );
                    }
                  }}
                />
              </TooltipComponent>
            </div>
            {strokeColor ? (
              <div className="monitor-progress-bundle">
                <LineProgressComponent
                  value={
                    bundleDeliveryStatus === 'finished'
                      ? 100
                      : completedPctDeliveries
                  }
                  variant="determinate"
                  customRootBarColor={strokeColor}
                />
              </div>
            ) : null}
          </div>
          {isBundleOpen && (
            <MonitorOrdersTableWrapper
              bundle={bundle}
              bundleId={bundleId}
              modeActive={bundleViewMode}
              setBundleViewMode={setBundleViewMode}
              toggleETAModal={toggleETAModal}
              toggleShelfModal={toggleShelfModal}
              dispatch={dispatch}
              region={geoZoneRegion}
              currentOrdersInstructions={currentOrders}
              updateSelectedBundleOrBundlesCallback={
                updateSelectedBundleOrBundlesCallback
              }
              isBundleLoading={isBundleLoading}
              isBundleOpen={isBundleOpen}
              lastSelectedOrders={lastSelectedOrders}
              toggleMoveOrdersModal={toggleMoveOrdersModal}
              togglePickupPointModal={togglePickupPointModal}
            />
          )}
        </div>
      );
    },
    checkProps
  );

MonitorCollapseWrapper.displayName = 'MonitorCollapseWrapper';
export { MonitorCollapseWrapper };
